import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { TCustomer } from '../schemas';

type CustomerCardProps = {
  customer: TCustomer;
};

// Helper function to format phone
const formatPhone = (phone: any, phoneNumber?: string): string => {
  // Try phone first
  if (phone) {
    if (typeof phone === 'string') return phone;
    if (typeof phone === 'object' && phone.number) {
      return phone.countryCode ? `${phone.countryCode} ${phone.number}` : phone.number;
    }
  }
  // Fallback to phoneNumber
  if (phoneNumber) return phoneNumber;
  return 'N/A';
};

// Helper function to format address
const formatAddress = (address: any): string => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  if (typeof address === 'object') {
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zip,
      address.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '';
  }
  return '';
};

const CustomerCard = ({ customer }: CustomerCardProps) => {
  const phoneDisplay = formatPhone(customer.phone, customer.phoneNumber);
  const addressDisplay = formatAddress(customer.address);
  return (
    <Box sx={styles.cardContainer}>
      {/* Name */}
      <Box sx={styles.dataColumnSmall}>
        <Typography sx={styles.label}>Name</Typography>
        <Typography sx={styles.value}>{customer.name ?? 'N/A'}</Typography>
      </Box>

      {/* Email */}
      <Box sx={styles.dataColumnLarge}>
        <Typography sx={styles.label}>Email</Typography>
        <Typography sx={styles.value}>{customer.email ?? 'N/A'}</Typography>
      </Box>

      {/* Phone */}
      <Box sx={styles.dataColumn}>
        <Typography sx={styles.label}>Phone</Typography>
        <Typography sx={styles.value}>{phoneDisplay}</Typography>
      </Box>

      {/* Address */}
      <Box sx={styles.dataColumnLarge}>
        <Typography sx={styles.label}>Address</Typography>
        <Typography sx={styles.value}>{addressDisplay || 'N/A'}</Typography>
      </Box>

      {/* Joined Date */}
      <Box sx={styles.dataColumnSmall}>
        <Typography sx={styles.label}>Join Date</Typography>
        <Typography sx={styles.value}>
          {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
        </Typography>
      </Box>

      {/* Customer ID */}
      <Box sx={styles.dataColumnSmall}>
        <Typography sx={styles.label}>Customer ID</Typography>
        <Typography sx={styles.value}>{customer._id ?? 'N/A'}</Typography>
      </Box>
    </Box>
  );
};

export default CustomerCard;

const styles = {
  cardContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    backgroundColor: '#FFF',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    padding: 0,
    overflow: 'hidden',
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      transform: 'translateY(-2px)',
      borderColor: '#d0d0d0',
    },
  },
  dataColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flex: 1,
    minWidth: 0,
    padding: '14px 12px',
    borderRight: '1px solid #f0f0f0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  dataColumnSmall: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flex: 0.8,
    minWidth: 0,
    padding: '14px 12px',
    borderRight: '1px solid #f0f0f0',
    '&:first-of-type': {
      backgroundColor: '#fafafa',
    },
  },
  dataColumnLarge: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flex: 1.3,
    minWidth: 0,
    padding: '14px 12px',
    borderRight: '1px solid #f0f0f0',
  },
  label: {
    fontFamily: 'GilroyBold',
    fontSize: 11,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    whiteSpace: 'nowrap',
    marginBottom: '2px',
  },
  value: {
    fontFamily: 'GilroyRegular',
    fontSize: 15,
    color: '#1a1a1a',
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.4,
  },
};
